import React, { useEffect, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  min2CharValidator,
  requiredArrayValidator,
  requiredValidator,
} from "../../components/formFields/CommonValidator";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import {
  createSignatureMoment,
  getAllSignatureMoments,
  getSignatureMomentByID,
  SignatureMomentImageUpload,
  updateSignatureMoment,
} from "./services/signatureMoment.service";
import { clearSignatureMomentDetails } from "./signatureMomentSlice";
import { RxCross1 } from "react-icons/rx";
import { closed } from "../../components/drawer/drawerSlice";
import IconButton from "../../components/common/IconButton";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import FormImageUpload from "../../components/formFields/FormImageUpload";
import { getFile } from "../../_helper/helper";

const CreateSignatureMoment: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  // const location = useLocation();
  // const SignatureMomentID = location.state?.SignatureMomentID;
  const SignatureMomentID = useAppSelector((state) => state.drawer.data);
  const SignatureMomentDetail = useAppSelector(
    (state) => state.signatureMoment.SignatureMomentDetail,
  );
  const loading = useAppSelector((state) => state.signatureMoment.formLoading);

  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [SignatureMomentDetail]);

  useEffect(() => {
    if (SignatureMomentID) {
      dispatch(getSignatureMomentByID(SignatureMomentID));
    }
    return () => {
      dispatch(clearSignatureMomentDetails());
    };
  }, [SignatureMomentID]);

  const handleSubmit = async (values: any) => {
    const formData = {
      Title: values?.Title ? values?.Title : "",
      Tag: values?.Tag ? values?.Tag : "",
      SequenceNo: values?.SequenceNo ? +values?.SequenceNo : null,
      IsActive: values?.IsActive === 1,
    };
    const ImageData = new FormData();
    const Image =
      values?.Image && typeof values?.Image !== "string"
        ? getFile(values?.Image)
        : "";

    try {
      if (SignatureMomentID) {
        const editPayload = {
          ...formData,
          ID: SignatureMomentID,
        };

        const response = await dispatch(updateSignatureMoment(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (values?.Image && typeof values?.Image !== "string") {
            ImageData.append("Image", Image);
            ImageData.append("ID", SignatureMomentID);
            await dispatch(SignatureMomentImageUpload(ImageData));
          }

          dispatch(closed());
          dispatch(getAllSignatureMoments());
          dispatch(clearSignatureMomentDetails());
        }
      } else {
        const response = await dispatch(createSignatureMoment(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            values?.Image &&
            typeof values?.Image !== "string" &&
            response?.payload?.Data?.ID
          ) {
            ImageData.append("Image", Image);
            ImageData.append("ID", response?.payload?.Data?.ID);
            await dispatch(SignatureMomentImageUpload(ImageData));
          }

          dispatch(closed());
          dispatch(getAllSignatureMoments());
          dispatch(clearSignatureMomentDetails());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  // if (loading) return <LoadingPanel gridRef={gridRef} />;
  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <div>
        <Form
          onSubmit={handleSubmit}
          key={formKey}
          initialValues={SignatureMomentDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <GridLayout
                style={{ marginRight: 10 }}
                gap={{ rows: 10, cols: 10 }}
                cols={[{ width: "50%" }, { width: "50%" }]}
              >
                <GridLayoutItem className="master-drawer-header" colSpan={2}>
                  <Typography.h4 className="m-0">
                    {SignatureMomentID
                      ? "Update Signature Moment"
                      : "Add Signature Moment"}
                  </Typography.h4>

                  <IconButton
                    type="button"
                    fillMode={"flat"}
                    themeColor={"dark"}
                    style={{ marginRight: 4 }}
                    onClick={() => dispatch(closed())}
                    icon={RxCross1}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={2}>
                  <Field
                    name="Title"
                    label="Title"
                    placeholder="Title"
                    component={FormTextField}
                    validator={min2CharValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={2}>
                  <Field name="Tag" label="Tag" component={FormTextField} />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    wrapperClassName="right-aligned-field"
                    name="SequenceNo"
                    label="Sequence No"
                    placeholder="0"
                    format="n0"
                    component={FormNumericTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name={`IsActive`}
                    label="Status"
                    placeholder="i.e. Active"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={STATUSARRAY?.map((status: any) => {
                      return {
                        value: status?.value,
                        label: status?.label,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={2}>
                  <Field
                    justifyContent="start"
                    name={`Image`}
                    label="Banner Image"
                    type="file"
                    alternateName="Banner Image"
                    component={FormImageUpload}
                    astrike={true}
                    validator={requiredArrayValidator}
                  />
                </GridLayoutItem>
                <GridLayoutItem
                  colSpan={2}
                  style={{
                    marginTop: "20px",
                  }}
                >
                  <div>
                    <ButtonWithLoading
                      label={SignatureMomentID ? "Update" : "Save"}
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || loading}
                      loading={loading}
                    />
                    <Button
                      type="button"
                      fillMode={"outline"}
                      themeColor={"primary"}
                      style={{ marginLeft: 10 }}
                      onClick={() => dispatch(closed())}
                    >
                      Cancel
                    </Button>
                  </div>
                </GridLayoutItem>
              </GridLayout>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateSignatureMoment;
